/**
 * Plantillas, formatos y helpers Lista Exprés (POS-PRICE-LISTS-001B).
 * Precios: resolveListaPrecio / resolveProductSalePrice — no persistir precios.
 */
import { resolveListaPrecio, buildEmpresaContacto } from './catalogoComercialExport';
import { computeAhorro as computeAhorroCore } from './listasExpresAhorro';

export const LISTAS_EXPRES_TEMPLATES = [
  { key: 'lista_limpia', label: 'Lista limpia', group: 'lista', maxProducts: 40 },
  { key: 'mayorista', label: 'Mayorista', group: 'lista', maxProducts: 40 },
  { key: 'catalogo_imagenes', label: 'Catálogo con imágenes', group: 'catalogo', maxProducts: 24 },
  { key: 'catalogo_sin_imagenes', label: 'Catálogo sin imágenes', group: 'lista', maxProducts: 40 },
  { key: 'novedades', label: 'Novedades', group: 'catalogo', maxProducts: 16 },
  { key: 'oferta_destacada', label: 'Oferta destacada', group: 'flyer', maxProducts: 8 },
  { key: 'flyer_uno', label: 'Flyer de un producto', group: 'flyer', maxProducts: 1 },
  { key: 'flyer_varios', label: 'Flyer de varios productos', group: 'flyer', maxProducts: 6 },
  { key: 'wa_vertical', label: 'WhatsApp vertical', group: 'whatsapp', maxProducts: 8 },
  { key: 'wa_cuadrado', label: 'WhatsApp cuadrado', group: 'whatsapp', maxProducts: 6 },
  { key: 'ig_post', label: 'Instagram post', group: 'social', maxProducts: 6 },
  { key: 'ig_historia', label: 'Historia vertical', group: 'social', maxProducts: 5 }
];

export const LISTAS_EXPRES_FORMATS = [
  { key: 'a4_vertical', label: 'A4 vertical', width: 794, height: 1123, kind: 'print' },
  { key: 'a4_horizontal', label: 'A4 horizontal', width: 1123, height: 794, kind: 'print' },
  { key: 'a5', label: 'A5', width: 559, height: 794, kind: 'print' },
  { key: 'carta', label: 'Carta', width: 816, height: 1056, kind: 'print' },
  { key: 'wa_vertical', label: 'WhatsApp vertical', width: 1080, height: 1920, kind: 'digital' },
  { key: 'wa_cuadrado', label: 'WhatsApp cuadrado', width: 1080, height: 1080, kind: 'digital' },
  { key: 'ig_post', label: 'Instagram post', width: 1080, height: 1080, kind: 'digital' },
  { key: 'ig_historia', label: 'Instagram historia', width: 1080, height: 1920, kind: 'digital' }
];

export const DEFAULT_VISIBLE_FIELDS = {
  logo: true,
  empresa: true,
  direccion: true,
  whatsapp: true,
  email: false,
  web: true,
  qr: true,
  titulo: true,
  subtitulo: true,
  vigencia: true,
  condiciones: false,
  codigo: true,
  stock: false,
  imagen: true,
  precio: true,
  precio_anterior: false,
  cta: true,
  leyenda: false
};

export function getTemplate(key) {
  return LISTAS_EXPRES_TEMPLATES.find((t) => t.key === key) || LISTAS_EXPRES_TEMPLATES[0];
}

export function getFormat(key) {
  return LISTAS_EXPRES_FORMATS.find((f) => f.key === key) || LISTAS_EXPRES_FORMATS[0];
}

export function orderProducts(productos, productOrder = []) {
  const map = new Map((productos || []).map((p) => [p.id, p]));
  const ordered = [];
  (productOrder || []).forEach((id) => {
    if (map.has(id)) {
      ordered.push(map.get(id));
      map.delete(id);
    }
  });
  map.forEach((p) => ordered.push(p));
  return ordered;
}

export function resolveExpresPrice(producto, priceListKey) {
  return resolveListaPrecio(producto, priceListKey || 'mayorista');
}

/** Precio anterior solo si existe campo verificable (no inventado). */
export function resolvePrecioAnterior(producto) {
  const candidates = [
    producto?.precio_anterior,
    producto?.precio_lista_anterior,
    producto?.listas_precios?.anterior
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

export function computeAhorroDisplay(producto, priceListKey) {
  const actual = resolveExpresPrice(producto, priceListKey);
  const anterior = resolvePrecioAnterior(producto);
  return computeAhorroCore(actual, anterior);
}

export function buildWhatsAppText({
  empresaConfig,
  commercialTitle,
  commercialSubtitle,
  validUntil,
  productos = [],
  priceListKey,
  highlightedProductIds = [],
  cta
}) {
  const contacto = buildEmpresaContacto(empresaConfig || {});
  const lines = [];
  if (contacto.nombre) lines.push(`*${contacto.nombre}*`);
  if (commercialTitle) lines.push(commercialTitle);
  if (commercialSubtitle) lines.push(commercialSubtitle);
  if (validUntil) lines.push(`Vigencia hasta ${validUntil}`);
  lines.push('');
  const highlight = new Set(highlightedProductIds || []);
  const top = (productos || [])
    .filter((p) => highlight.size === 0 || highlight.has(p.id))
    .slice(0, 8);
  top.forEach((p) => {
    const precio = resolveExpresPrice(p, priceListKey);
    const money = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(
      Number(precio) || 0
    );
    lines.push(`• ${p.nombre || 'Producto'} — ${money}`);
  });
  lines.push('');
  if (cta) lines.push(cta);
  if (contacto.direccion) lines.push(contacto.direccion);
  if (contacto.whatsapp) lines.push(`WhatsApp: ${contacto.whatsapp}`);
  if (contacto.web) lines.push(contacto.web);
  return lines.filter(Boolean).join('\n');
}

export { buildEmpresaContacto, resolveListaPrecio };
